import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { debounce } from "lodash";
import { api } from "../api";
import { getUser } from "../utils";
import { ShowToast } from "../../components/toast";
import { expandChangedDates } from "../../pages/calendar/utils/dateRangeUtils";
import { useDayMetrics } from "./use-day-metrics";
import { useAnalytics } from "./use-analytics";
import {
  useMlosRestrictions,
  useMlosConfig,
  useSetManualMlos,
  useRemoveManualMlos,
} from "./use-mlos";
import {
  calculateNewPrice,
  computeDerivedNewPrice,
  cascadeConstraint,
  validatePrice,
  buildSavePayload,
  PriceEditorData,
  PriceForm,
  PriceFormRoom,
} from "../../pages/calendar/utils/pricing-calc";

interface Args {
  selectedDate?: string;
  data: PriceEditorData;
  onUpdate: (changed: string[]) => void;
}

interface ActiveMlos {
  minLos: number;
  source: string;
}

export interface PriceAdjustmentMlos {
  isEnabled: boolean;
  active: ActiveMlos | null;
  value: number;
  setValue: (n: number) => void;
  editing: boolean;
  setEditing: (b: boolean) => void;
  set: () => Promise<void>;
  remove: () => Promise<void>;
  daysOut: number;
  minDaysOut: number;
  settingLoading: boolean;
  removingLoading: boolean;
}

export interface UsePriceAdjustmentReturn {
  form: PriceForm;
  priceError: string;
  saving: boolean;
  setBaseNewPrice: (value: string) => void;
  setRoomDifferential: (roomId: string, value: string) => void;
  setRoomConstraint: (
    roomId: string,
    key: "rateFloor" | "rateCeiling",
    value: string
  ) => void;
  setBaseConstraint: (which: "rateFloor" | "rateCeiling", value: string) => void;
  toggleRelativeFixed: () => void;
  toggleBulk: () => void;
  setSingleDate: (date: string) => void;
  setBulkRange: (start: string, end: string, excluded: string[]) => void;
  handleSave: () => Promise<void>;
  handleReset: () => Promise<void>;
  mlos: PriceAdjustmentMlos;
}

export function usePriceAdjustment({
  selectedDate,
  data,
  onUpdate,
}: Args): UsePriceAdjustmentReturn {
  const user = getUser();
  const { trackEvent } = useAnalytics();
  const { data: dayMetrics, refetch } = useDayMetrics(
    user?.id,
    selectedDate || moment().format("YYYY-MM-DD")
  );
  const [saving, setSaving] = useState(false);
  const [priceError, setPriceError] = useState<string>("");

  const { data: mlosData, refetch: refetchMlos } = useMlosRestrictions(
    user?.id || "",
    selectedDate,
    selectedDate
  );
  const { data: mlosConfig } = useMlosConfig(user?.id || "");
  const setManualMlos = useSetManualMlos();
  const removeManualMlos = useRemoveManualMlos();
  const activeMlosRaw = mlosData?.find?.((r) => !r.removedAt);
  const [mlosValue, setMlosValue] = useState<number>(activeMlosRaw?.minLos || 2);
  const [mlosEditing, setMlosEditing] = useState(false);
  const isMlosEnabled = mlosConfig?.enableMlos ?? false;
  const daysOut = selectedDate
    ? moment(selectedDate).diff(moment(), "days")
    : 0;
  const minDaysOut = mlosConfig?.minDaysOut ?? 7;

  const [form, setForm] = useState<PriceForm>(() => ({
    ...(data || {}),
    relativePrice: true,
    bulk: false,
    rateFloor:
      data?.rateFloor != null && data?.rateFloor !== ""
        ? String(data.rateFloor)
        : "",
    rateCeiling:
      data?.rateCeiling != null && data?.rateCeiling !== ""
        ? String(data.rateCeiling)
        : "",
    baseRoomTypeNewPrice: "",
    startDate: moment(selectedDate).format("YYYY-MM-DD"),
    endDate: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
    excludedDates: [] as string[],
    roomTypes:
      (data?.roomTypes ?? []).map((room) => ({
        ...room,
        newPrice: "0",
        newFixedPrice: "",
        rateFloor: room?.rateFloor || "",
        rateCeiling: room?.rateCeiling || "",
      })) ?? [],
  }));

  // Track original room type prices to detect changes
  const [originalRoomPrices] = useState<Record<string, number>>(() =>
    (data?.roomTypes ?? []).reduce((acc, room) => {
      acc[room?.id] = room?.rate ?? 0;
      return acc;
    }, {} as Record<string, number>)
  );

  // Fetch the correct floor/ceiling values for the specific date.
  // Daily overrides take precedence over seasonal floors.
  useEffect(() => {
    if (dayMetrics) {
      setForm((prev) => ({
        ...prev,
        rateFloor: dayMetrics.rateFloor || "",
        rateCeiling: dayMetrics.rateCeiling || "",
        roomTypes: (prev.roomTypes ?? []).map((room) => {
          const dayMetricRoom = dayMetrics.roomTypes?.find(
            (r: { id?: string }) => r?.id === room?.id
          );
          return {
            ...room,
            rateFloor: dayMetricRoom?.rateFloor || "",
            rateCeiling: dayMetricRoom?.rateCeiling || "",
          };
        }),
      }));
    }
  }, [dayMetrics]);

  // Stable debounced validation function persisting across renders.
  const debouncedValidatePrice = useMemo(
    () =>
      debounce((price: string, floor: number, ceiling: number) => {
        setPriceError(validatePrice(price, floor, ceiling));
      }, 1000),
    []
  );

  useEffect(() => {
    return () => {
      debouncedValidatePrice.cancel();
    };
  }, [debouncedValidatePrice]);

  const runValidation = (price: string) => {
    const baseRoom = form?.roomTypes?.find(
      (r) => r.id === data?.baseRoomTypeId
    );
    const floor = Number(baseRoom?.rateFloor || 0);
    const ceiling = Number(baseRoom?.rateCeiling || 0);
    debouncedValidatePrice(price, floor, ceiling);
  };

  // Re-validate price when base room floor or ceiling changes.
  const baseRoomFloor = form?.roomTypes?.find(
    (r) => r.id === data?.baseRoomTypeId
  )?.rateFloor;
  const baseRoomCeiling = form?.roomTypes?.find(
    (r) => r.id === data?.baseRoomTypeId
  )?.rateCeiling;
  useEffect(() => {
    if (
      form?.baseRoomTypeNewPrice &&
      form?.baseRoomTypeNewPrice !== "0" &&
      form?.baseRoomTypeNewPrice !== ""
    ) {
      const baseRoom = form?.roomTypes?.find(
        (r) => r.id === data?.baseRoomTypeId
      );
      const floor = Number(baseRoom?.rateFloor || 0);
      const ceiling = Number(baseRoom?.rateCeiling || 0);
      debouncedValidatePrice(form.baseRoomTypeNewPrice, floor, ceiling);
    }
    // Include the new price so a simultaneous price + floor/ceiling change
    // validates against the current price, not a stale one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRoomFloor, baseRoomCeiling, form?.baseRoomTypeNewPrice]);

  // ── Handlers ──────────────────────────────────────────────────

  const setBaseNewPrice = (value: string) => {
    runValidation(value);
    setForm((prev) => ({
      ...prev,
      baseRoomTypeNewPrice: value || "",
      roomTypes: (prev.roomTypes ?? []).map((room) => ({
        ...room,
        newPrice: prev.relativePrice
          ? calculateNewPrice(
              prev.relativePrice ? String(room?.percentage ?? "") : room?.newRelativePrice ?? "",
              value,
              prev.relativePrice
            )
          : String(Number(value) + Number(room?.fixed ?? 0)),
      })),
    }));
  };

  const setRoomDifferential = (roomId: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      roomTypes: (prev.roomTypes ?? []).map((r) => {
        if (r?.id !== roomId) return r;
        const baseRate =
          prev?.baseRoomTypeNewPrice === "" ||
          prev?.baseRoomTypeNewPrice === "0" ||
          prev?.baseRoomTypeNewPrice == null
            ? data?.baseRoomTypeRate ?? 0
            : prev?.baseRoomTypeNewPrice;
        const baseRateNum = Number(baseRate) || 0;
        return {
          ...r,
          percentage: prev.relativePrice ? (value as unknown as number) : r.percentage,
          fixed: prev.relativePrice ? r.fixed : (value as unknown as number),
          newPrice: computeDerivedNewPrice(
            baseRateNum,
            Number(value),
            prev.relativePrice
          ),
        };
      }),
    }));
  };

  const setRoomConstraint = (
    roomId: string,
    key: "rateFloor" | "rateCeiling",
    value: string
  ) => {
    setForm((prev) => {
      const isBase = roomId === data?.baseRoomTypeId;
      if (!isBase) {
        return {
          ...prev,
          roomTypes: (prev.roomTypes ?? []).map((r) =>
            r?.id === roomId ? { ...r, [key]: value } : r
          ),
        };
      }
      // Editing the base constraint cascades to derived rooms.
      const baseConstraint = Number(value || 0);
      return {
        ...prev,
        roomTypes: (prev.roomTypes ?? []).map((r) => {
          if (r?.id === data?.baseRoomTypeId) {
            return { ...r, [key]: value };
          }
          if (baseConstraint > 0) {
            const differential = prev.relativePrice
              ? Number(r?.percentage || 0)
              : Number(r?.fixed || 0);
            return {
              ...r,
              [key]: cascadeConstraint(
                baseConstraint,
                differential,
                prev.relativePrice
              ),
            };
          }
          return { ...r, [key]: "" };
        }),
      };
    });
  };

  const setBaseConstraint = (
    which: "rateFloor" | "rateCeiling",
    value: string
  ) => {
    setRoomConstraint(data?.baseRoomTypeId, which, value);
  };

  const toggleRelativeFixed = () => {
    setForm((prev) => {
      if (!prev.relativePrice) {
        // currently fixed -> switching to relative
        const price =
          prev?.baseRoomTypeNewPrice === "" ||
          prev?.baseRoomTypeNewPrice === "0" ||
          prev?.baseRoomTypeNewPrice == null
            ? `${data?.baseRoomTypeRate ?? 0}`
            : prev?.baseRoomTypeNewPrice;
        const priceNum = Number(price) || 0;
        return {
          ...prev,
          baseRoomTypeNewPrice: price,
          relativePrice: !prev.relativePrice,
          roomTypes: (prev.roomTypes ?? []).map((room) => ({
            ...room,
            newRelativePrice: "",
            newPrice:
              prev?.baseRoomTypeNewPrice &&
              prev?.baseRoomTypeNewPrice !== "0"
                ? prev?.relativePrice
                  ? String(
                      Math.round(
                        priceNum +
                          (Number(room?.percentage ?? 0) / 100) * priceNum
                      )
                    )
                  : String(Math.round(priceNum + Number(room?.fixed ?? 0)))
                : "0",
          })),
        };
      }
      // currently relative -> switching to fixed
      const price =
        prev?.baseRoomTypeNewPrice === "" ||
        prev?.baseRoomTypeNewPrice === "0" ||
        prev?.baseRoomTypeNewPrice == null
          ? `${data?.baseRoomTypeRate ?? 0}`
          : prev?.baseRoomTypeNewPrice;
      const priceNum = Number(price) || 0;
      return {
        ...prev,
        relativePrice: !prev.relativePrice,
        roomTypes: (prev.roomTypes ?? []).map((room) => {
          const originalData = data?.roomTypes?.find(
            (r) => r?.name === room?.name
          );
          return {
            ...room,
            percentage: originalData?.percentage ?? 0,
            newPrice:
              prev?.baseRoomTypeNewPrice &&
              prev?.baseRoomTypeNewPrice !== "0"
                ? !prev?.relativePrice
                  ? String(
                      Math.round(
                        priceNum +
                          (Number(room?.percentage ?? 0) / 100) * priceNum
                      )
                    )
                  : String(Math.round(priceNum + Number(room?.fixed ?? 0)))
                : "0",
          };
        }),
      };
    });
  };

  const toggleBulk = () => {
    setForm((prev) => ({
      ...prev,
      bulk: !prev.bulk,
      startDate: moment(selectedDate).format("YYYY-MM-DD"),
      endDate: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
      excludedDates: [],
    }));
  };

  const setSingleDate = (date: string) => {
    setForm((prev) => ({
      ...prev,
      startDate: moment(date).format("YYYY-MM-DD"),
      endDate: moment(date).format("YYYY-MM-DD"),
      date: moment(date).format("YYYY-MM-DD"),
    }));
  };

  const setBulkRange = (start: string, end: string, excluded: string[]) => {
    setForm((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
      excludedDates: excluded,
    }));
  };

  const handleReset = async () => {
    const { data: freshData } = await refetch();
    if (!freshData) return;

    const resetFloor = freshData.rateFloor || "";
    const resetCeiling = freshData.rateCeiling || "";

    setForm({
      ...freshData,
      baseRoomTypeRate: freshData?.baseRoomTypeRate ?? 0,
      relativePrice: true,
      bulk: false,
      rateFloor: resetFloor,
      rateCeiling: resetCeiling,
      baseRoomTypeNewPrice: "",
      startDate: moment(selectedDate).format("YYYY-MM-DD"),
      endDate: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
      excludedDates: [],
      roomTypes:
        (freshData?.roomTypes ?? []).map((room: PriceFormRoom) => ({
          ...room,
          newPrice: "0",
          fixed: room?.fixed ?? 0,
          percentage: room?.percentage ?? 0,
          newFixedPrice: "",
          rateFloor: room?.rateFloor || "",
          rateCeiling: room?.rateCeiling || "",
        })) ?? [],
    } as PriceForm);

    setPriceError("");
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const { payload, floorCeiling } = buildSavePayload(form, data, selectedDate);

      if (form?.bulk) {
        await api.overrideBulkFloorCeiling(user?.id, floorCeiling);
      } else {
        await api.overrideFloorCeiling(user?.id, floorCeiling);
      }

      // Only call saveDayPrice if the user entered a real base price change.
      const userEnteredNewPrice =
        form?.baseRoomTypeNewPrice &&
        form?.baseRoomTypeNewPrice !== "" &&
        form?.baseRoomTypeNewPrice !== "0";

      const hasRoomPriceChanges =
        !!userEnteredNewPrice &&
        payload.roomTypePrices.some((roomTypePrice) => {
          const originalPrice = originalRoomPrices[roomTypePrice.roomTypeId];
          const newPrice = Number(roomTypePrice.price);
          return originalPrice !== newPrice;
        });

      if (hasRoomPriceChanges) {
        await api.saveDayPrice(user?.id, payload);
      }

      // Single-day uses the selected date (form.date is unset)
      const syncDates = {
        startDate: moment(
          form?.bulk ? form?.startDate : form?.date ?? selectedDate
        ).format("YYYY-MM-DD"),
        endDate: moment(
          form?.bulk ? form?.endDate : form?.date ?? selectedDate
        ).format("YYYY-MM-DD"),
      };
      await api.syncRatesGrouped(user?.id, syncDates);

      const changedDates = form?.bulk
        ? expandChangedDates({
            startDate: moment(form.startDate).format("YYYY-MM-DD"),
            endDate: moment(form.endDate).format("YYYY-MM-DD"),
            excludedDates: (form.excludedDates ?? []).map((d: string) =>
              moment(d).format("YYYY-MM-DD")
            ),
          })
        : [moment(form?.date ?? selectedDate).format("YYYY-MM-DD")];

      onUpdate(changedDates);
      setSaving(false);

      trackEvent("price_adjustment_saved", {
        adjustmentType: form?.bulk ? "bulk" : "single",
      });

      ShowToast({
        type: "success",
        message: "Prices saved successfully",
      });
    } catch (e: unknown) {
      setSaving(false);
      const err = e as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (err?.response?.status === 400) {
        ShowToast({
          type: "error",
          message: err?.response?.data?.message || "Invalid price settings",
        });
      } else {
        ShowToast({
          type: "error",
          message:
            err?.response?.data?.message ||
            "An error occurred while saving prices",
        });
      }
    }
  };

  const activeMlos: ActiveMlos | null = activeMlosRaw
    ? { minLos: activeMlosRaw.minLos, source: activeMlosRaw.source }
    : null;

  const mlos: PriceAdjustmentMlos = {
    isEnabled: isMlosEnabled,
    active: activeMlos,
    value: mlosValue,
    setValue: setMlosValue,
    editing: mlosEditing,
    setEditing: setMlosEditing,
    set: async () => {
      await setManualMlos.mutateAsync({
        userId: user?.id,
        date: selectedDate || "",
        minLos: mlosValue,
      });
      refetchMlos();
    },
    remove: async () => {
      await removeManualMlos.mutateAsync({
        userId: user?.id,
        date: selectedDate || "",
      });
      refetchMlos();
    },
    daysOut,
    minDaysOut,
    settingLoading: setManualMlos.isLoading,
    removingLoading: removeManualMlos.isLoading,
  };

  return {
    form,
    priceError,
    saving,
    setBaseNewPrice,
    setRoomDifferential,
    setRoomConstraint,
    setBaseConstraint,
    toggleRelativeFixed,
    toggleBulk,
    setSingleDate,
    setBulkRange,
    handleSave,
    handleReset,
    mlos,
  };
}
